import {AfterViewInit, Component, Inject, Input, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {BehaviorSubject, debounceTime, distinctUntilChanged, finalize, firstValueFrom, forkJoin, from, Subject, takeUntil} from 'rxjs';
import {Neighborhood} from '../../../../models/neighborhood';
import {User} from '../../../../models/user';
import {Region} from '../../../../models/region';
import {SearchService} from '../../../../services/search.service';
import {ActivatedRoute, Router} from '@angular/router';
import {RegionService} from '../../../../services/region-service';
import {FilterService} from '../../../../services/filter.service';
import {AuthService} from '../../../../services/auth.service';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {isPlatformBrowser, NgForOf, NgIf} from '@angular/common';
import {Search, SearchType} from '../../../../models/search';
import jQuery from 'jquery'

@Component({
  selector: 'app-place-list-search',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgForOf,
    NgIf
  ],
  templateUrl: './place-list-search.component.html',
  styleUrl: './place-list-search.component.scss'
})
export class PlaceListSearchComponent implements OnInit, AfterViewInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  @Input() searchType = 'place';

  searchText$ = new BehaviorSubject<string>("");

  searchText: string | null = "";
  placeholder: string | null = "";

  // Region
  isRegionLoading = false
  isRegionLoaded = false
  regionSlug: string | null = null
  region: Region | null = null

  // Neighborhood
  neighborhoodSlug: string | null = null
  neighborhood: Neighborhood | null = null
  neighborhoods: Neighborhood[] = []

  // User
  currentUser: User | null = null

  searchesByRegion: Search[] = []
  searches: Search[] = []

  autoCompleteSessionToken: google.maps.places.AutocompleteSessionToken | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
              private router: Router,
              private route: ActivatedRoute,
              private searchService: SearchService,
              private regionService: RegionService,
              private filterService: FilterService,
              private authService: AuthService) {
  }

  ngOnInit() {
    this.authService.currentUser$.pipe(takeUntil(this.onDestroy$)).subscribe(currentUser => {
      if (currentUser) this.currentUser = currentUser;
    })

    // Listen to route change
    this.route.params
      .pipe(
        takeUntil(this.onDestroy$),
      ).subscribe(async params => {
      this.regionSlug = params['regionSlug']
      this.neighborhoodSlug = params['neighborhoodSlug']

      // Region slug isn't available try to get region based on current session
      if (!this.regionSlug) {
        this.regionSlug = this.regionService.regionSlug$.value
      }

      // Region slug still isn't available try to get region based on current user region or ip address
      if (!this.regionSlug) {
        this.regionSlug = await this.regionService.getRegionSlug()
      }

      // Region
      if (this.regionSlug) {
        this.region = await this.refreshRegion(this.regionSlug)
      }

      // Listen to neighborhood change
      this.filterService.neighborhoods$.pipe(takeUntil(this.onDestroy$)).subscribe(neighborhoods => {
        this.neighborhoods = neighborhoods
        this.refreshPlaceholder();
      })
    });

    // Search text listener
    this.searchText$.pipe(
      takeUntil(this.onDestroy$),
      debounceTime(400),      // Preventing fast searches
      distinctUntilChanged()) // Preventing duplicate searches
      .subscribe(searchText => {
        this.refreshSearches(searchText)
      })

    // Initialize region search results
    const showInvisible = false;
    const showExclusive = !!this.currentUser // Only for customers
    this.regionService.findRegions(showInvisible, showExclusive).pipe(
      takeUntil(this.onDestroy$))
      .subscribe(regions => {
        regions.forEach(region => {
          this.searchesByRegion.push({type: SearchType.region, title: region.name!, url: `/discover/${region.slug}`, data: region});
        });
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  async refreshRegion(regionSlug: string) {
    this.isRegionLoading = true
    return firstValueFrom(this.regionService.getBySlug(regionSlug).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => {
        this.isRegionLoading = false
        this.isRegionLoaded = true
      })))
  }

  refreshPlaceholder() {
    if (this.neighborhoodSlug && this.neighborhoods.length > 0) {
      this.neighborhood = this.neighborhoods.find(n => n.slug === this.neighborhoodSlug) || null;
    } else {
      this.neighborhood = null;
    }

    if (this.region && this.neighborhood) {
      this.placeholder = this.region.name + ' → ' + this.neighborhood.name;
    } else if (this.region) {
      this.placeholder = this.region.name!
    } else {
      this.placeholder = '';
    }
  }

  onSearchTextInputChange(event: KeyboardEvent) {
    this.searchText = (event.target as HTMLInputElement).value;
    this.searchText$.next(this.searchText)
  }

  refreshSearches(searchText: string) {
    forkJoin([
      from(this.searchInitialRegions(searchText)),
      from(this.searchLocations(searchText)),
      from(this.searchPlaceAutoComplete(searchText))
    ]).pipe(takeUntil(this.onDestroy$))
      .subscribe((results) => {
        const searchRegionsResults = results[0] || []
        const searchLocationsResults = results[1] || []
        const searchPlaceAutoCompleteResults = results[2] || []

        this.searches = [...searchRegionsResults, ...searchLocationsResults, ...searchPlaceAutoCompleteResults]
      })
  }

  async searchInitialRegions(searchText: string) {
    if (searchText.trim() === '') {
      return Promise.resolve(this.searchesByRegion)
    }

    // Use searchLocations to obtain region and place information
    return Promise.resolve([])
  }

  async searchLocations(searchText: string) {
    if (searchText.trim() === '') {
      return Promise.resolve([])
    }

    const showInvisible = false;
    const showNoSeats = false;

    return firstValueFrom(this.searchService.findLocations(searchText, showInvisible, showNoSeats))
  }

  async searchPlaceAutoComplete(searchText: string) {
    if (searchText.trim() === '') {
      return Promise.resolve([])
    }

    // Initialize google libraries
    const {AutocompleteSessionToken, AutocompleteSuggestion} = await google.maps.importLibrary("places") as google.maps.PlacesLibrary;

    // Initialize new auto complete session token
    if (!this.autoCompleteSessionToken) {
      this.autoCompleteSessionToken = new AutocompleteSessionToken();
    }

    // Generate request
    let request = {
      sessionToken: this.autoCompleteSessionToken,
      language: "en-US",
      input: searchText,
      // locationRestriction: { west: -122.44, north: 37.8, east: -122.39, south: 37.78 },
      // origin: { lat: 37.7893, lng: -122.4039 },
      // includedPrimaryTypes: ["restaurant"],
      // region: "us",
    };

    const {suggestions} = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

    const results: Search[] = []

    for (let suggestion of suggestions) {
      const placePredictionId = suggestion.placePrediction?.placeId
      const placePredictionText = suggestion.placePrediction?.text.text;
      const placePredictionData = suggestion.placePrediction?.toPlace()

      if (placePredictionId && placePredictionText && placePredictionData) {
        results.push({type: SearchType.prediction, title: placePredictionText, url: "#", data: placePredictionData})
      }
    }

    return Promise.resolve(results)
  }

  async selectSearch(search: Search) {
    if (search.type === 'region') {
      this.router.navigate([search.url]).finally()
    }

    if (search.type === 'place') {
      this.router.navigate([search.url]).finally()
    }

    if (search.type === 'prediction') {
      const place: google.maps.places.Place = search.data

      // Make API call to obtain location data
      await place.fetchFields({fields: ['location']})

      // Resetting search session
      this.autoCompleteSessionToken = null

      // Get data
      this.router.navigate(["/discover"], {
        queryParams: {
          latitude: place.location?.lat(),
          longitude: place.location?.lng()
        }
      }).finally()
    }
  }

  toggleSearchResultShow() {
    if (isPlatformBrowser(this.platformId)) {
      jQuery(document).ready(() => {
        if (!jQuery("#searchResults").hasClass("show")) {
          jQuery("#searchResults").addClass("show")
        }
      });
    }
  }

  ngAfterViewInit(): void {

    // Hide the navbar dropdown if it's shown
    if (isPlatformBrowser(this.platformId)) {
      jQuery(document).ready(() => {
        jQuery(document).unbind("click")
        jQuery(document).click((event: any) => {
          let isNotClickOnSearchInput = !jQuery(event.target).hasClass("form-control")
          let isNotClickOnDropdownMenu = !jQuery(event.target).hasClass("dropdown-link")
          let isDropdownMenuOpened = jQuery("#searchResults").hasClass("show")
          if (isNotClickOnSearchInput && isNotClickOnDropdownMenu && isDropdownMenuOpened) {
            jQuery("#searchResults").removeClass("show")
          }
        });
      });
    }
  }

}
